import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import DataTable from 'react-data-table-component';
import { 
  Box, TextField, Button, IconButton, Modal,
  Typography, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset'; // Importar ícono de reseteo
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/AppStyle.css';

const UserPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los usuarios');
      setLoading(false);
    }
  };

  const getStatusText = (idStatus) => {
    return idStatus === 1 ? 'Activo' : 'Inactivo';
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setName(user.name);
    setIdentifier(user.identifier);
    setEmail(user.email);
    setTelephone(user.telephone);
    setPassword(user.password);
    setIsModalOpen(true);
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };
  
  const handleReset = (user) => {
    setSelectedUser(user);
    setIsResetPasswordOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(userToDelete.id);
      setIsDeleteDialogOpen(false);
      fetchUsers();
      toast.success('Usuario eliminado con éxito');
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  const confirmReset = async () => {
    try {
      await userService.resetPassword(selectedUser.id, selectedUser.identifier);
      setIsResetPasswordOpen(false);
      fetchUsers();
      toast.success('Contraseña reseteada con éxito');
    } catch (err) {
      setError('Error al resetear clave usuario');
    }
  };

  const handleViewDetail = (user) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setName('');
    setIdentifier('');
    setEmail('');
    setTelephone('');
    setPassword('');
    setIsModalOpen(true);
  };

  const handleSaveUser = async () => {
    const userDTO = {
      name,
      identifier,
      email,
      telephone,
      password,
    };

    try {
      if (selectedUser) {
        await userService.updateUser(selectedUser.id, userDTO);
      } else {
        await userService.register(userDTO);
      }
      setIsModalOpen(false);
      fetchUsers();
      toast.success('Usuario guardado con éxito');
    } catch (err) {
      setError('Error al guardar el usuario');
    }
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
    },
    {
      name: 'Nombre',
      selector: row => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Teléfono',
      selector: row => row.telephone,
      sortable: true,
    },
    {
      name: 'Estado',
      selector: row => getStatusText(row.idStatus),
      sortable: true,
      cell: row => (
        <span className={`apple-badge ${row.idStatus === 1 ? 'apple-badge-success' : 'apple-badge-error'}`}>
          {getStatusText(row.idStatus)}
        </span>
      ),
    },
    {
      name: 'Acciones',
      cell: row => (
        <Box>
          <IconButton onClick={() => handleViewDetail(row)} size="small" className="apple-button apple-tooltip">
            <VisibilityIcon fontSize="small" />
            <span className="tooltiptext">Ver Detalle</span>
          </IconButton>
          <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
            <EditIcon fontSize="small" />
            <span className="tooltiptext">Editar</span>
          </IconButton>
          <IconButton onClick={() => handleReset(row)} size="small" className="apple-button apple-tooltip">
            <LockResetIcon fontSize="small" />
            <span className="tooltiptext">Resetear Clave</span>
          </IconButton>
          <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
            <DeleteIcon fontSize="small" />
            <span className="tooltiptext">Eliminar</span>
          </IconButton>
        </Box>
      ),
      width: '200px', // Ajuste del ancho de la columna de acciones
    },
  ];

  const filteredItems = users.filter(
    item => item.name.toLowerCase().includes(filterText.toLowerCase()) ||
            item.email.toLowerCase().includes(filterText.toLowerCase())
  );

  const customStyles = {
    table: {
      style: {
        minWidth: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#F2F2F7',
        borderBottom: 'none',
      },
    },
    headCells: {
      style: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868B',
        textTransform: 'uppercase',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        color: '#1D1D1F',
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
        <div className="apple-search-container">
          <SearchIcon className="apple-search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre o email"
            onChange={e => setFilterText(e.target.value)}
            className="apple-search-input"
          />
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          className="apple-button"
        >
          Agregar Usuario
        </Button>
      </Box>
    );
  }, [filterText]);

  return (
    <div className="apple-container">
      <Paper className="apple-card">
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          progressPending={loading}
          persistTableHead
          customStyles={customStyles}
        />
      </Paper>

      <ToastContainer />

      {/* Modal para editar/crear usuario */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            {selectedUser ? 'Editar Usuario' : 'Agregar Usuario'}
          </Typography>

          <div className="apple-modal-content">
            <TextField
              label="Nombre"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Identificador"
              fullWidth
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Teléfono"
              fullWidth
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Contraseña"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveUser}
              className="apple-button"
              fullWidth
              sx={{ mt: 2 }}
            >
              Guardar
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Dialog para eliminar usuario */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar al usuario "{userToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} className="apple-button">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para resetear contraseña */}
      <Dialog
        open={isResetDialogOpen}
        onClose={() => setIsResetPasswordOpen(false)}
      >
        <DialogTitle>Confirmar Reseteo de Clave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea resetear la clave del usuario "{selectedUser?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResetPasswordOpen(false)} className="apple-button">
            Cancelar
          </Button>
          <Button onClick={confirmReset} className="apple-button">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para ver detalle del usuario */}
      <Modal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            Detalles del Usuario
          </Typography>
    
          <div className="apple-modal-content">
            <TextField
              label="Nombre"
              fullWidth
              value={selectedUser?.name}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Identificador"
              fullWidth
              value={selectedUser?.identifier}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Email"
              fullWidth
              value={selectedUser?.email}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Teléfono"
              fullWidth
              value={selectedUser?.telephone}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Estado"
              fullWidth
              value={getStatusText(selectedUser?.idStatus)}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div className="apple-modal-footer">
            <Button onClick={() => setIsDetailModalOpen(false)} className="apple-button">
              Cerrar
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default UserPage;
